import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns';
import { Avatar } from '@components/avatar';
import { Button } from '@components/ui/button';
import { ActionSheet } from '@components/scheduling/actionSheet';
import { formatTenantTime, shortDate, shortDay } from '@utils/i18n';
import { MonthGrid } from '../monthGrid';
import { PhoneConflictCalendarProps } from './types';

export const PhoneConflictCalendar = ({ target, days, onDismiss }: PhoneConflictCalendarProps) => {
  const { t } = useTranslation('app');
  const [month, setMonth] = useState(() => parseISO(days[0] || ''));
  const [picked, setPicked] = useState<string>();
  const daySet = new Set(days);
  const conflictDays = new Set(target?.hits.map((hit) => hit.date));
  const busyCount = days.filter((day) => conflictDays.has(day)).length;
  const pickedHits = target?.hits.filter((hit) => hit.date === picked) || [];

  useEffect(() => {
    if (!target) return;
    setPicked(undefined);
    const firstConflict = days.find((day) => conflictDays.has(day)) || days[0];
    if (firstConflict) setMonth(parseISO(firstConflict));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return (
    <ActionSheet
      open={Boolean(target)}
      onDismiss={onDismiss}
      title={
        <span className="flex items-center gap-2.5">
          <Avatar name={target?.name || ''} hideTooltip className="h-8 w-8 text-sm" />
          <span className="flex min-w-0 flex-col">
            <span className="truncate">{target?.name}</span>
            <span className="text-[13px] font-normal text-neutral-500">
              {t('scheduling.phone.conflictDays', { count: busyCount, total: days.length })}
            </span>
          </span>
        </span>
      }
      actions={
        <div className="!grid-cols-1">
          <Button variant="secondary" onClick={onDismiss}>
            {t('actions.close')}
          </Button>
        </div>
      }>
      <div className="flex flex-col gap-2.5 pr-3">
        <div className="flex gap-4 text-xs text-neutral-500">
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-full bg-primary-500" />
            {t('scheduling.phone.filters.available')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-full bg-warning-500" />
            {t('scheduling.phone.conflict')}
          </span>
        </div>
        <div className="rounded-xl border border-neutral-200 px-2.5 pb-2 pt-1">
          <MonthGrid
            month={month}
            onMonthChange={setMonth}
            onTap={(key) => conflictDays.has(key) && setPicked(key)}
            getDay={(key) => ({
              selected: daySet.has(key),
              tone: conflictDays.has(key) ? 'warning' : 'primary',
              disabled: !daySet.has(key) || !conflictDays.has(key),
              label: conflictDays.has(key) ? t('scheduling.phone.conflict') : undefined,
            })}
          />
        </div>
        {picked && (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-neutral-700">
              {shortDay(parseISO(picked))}, {shortDate(parseISO(picked))}
            </span>
            {pickedHits.map((hit) => (
              <div
                key={hit.scheduleId}
                className="flex flex-col rounded-md border border-warning-200 bg-warning-50 px-2.5 py-1.5 text-xs leading-tight text-neutral-800">
                <span className="font-bold">
                  {formatTenantTime(hit.startsAt)} - {formatTenantTime(hit.endsAt)}
                </span>
                <span className="font-medium">
                  {[hit.taskName, hit.job].filter(Boolean).join(' · ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </ActionSheet>
  );
};
